import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { AttachFile, Delete, Download, CloudUpload } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@viilutalous/common';
import { rowAttachmentsAPI } from './expensesAPI';

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AttachmentDialog({ open, onClose, tenantId, rowId, readOnly, onCountChange }) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const fileInputRef = useRef(null);

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open && rowId && tenantId) {
      fetchAttachments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rowId, tenantId]);

  const fetchAttachments = async () => {
    try {
      setLoading(true);
      const response = await rowAttachmentsAPI.getByRow(tenantId, rowId);
      setAttachments(response.data.attachments || []);
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      await rowAttachmentsAPI.upload(tenantId, rowId, file);
      showSuccess(t('attachments.uploaded'));
      await fetchAttachments();
      if (onCountChange) onCountChange(attachments.length + 1);
    } catch (error) {
      console.error('Error uploading attachment:', error);
      showError(t('attachments.upload_error'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (attachmentId) => {
    try {
      await rowAttachmentsAPI.delete(tenantId, attachmentId);
      showSuccess(t('attachments.deleted'));
      const updated = attachments.filter(a => a.id !== attachmentId);
      setAttachments(updated);
      if (onCountChange) onCountChange(updated.length);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      showError(t('attachments.delete_error'));
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const response = await rowAttachmentsAPI.getFile(tenantId, attachment.id);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      showError(t('attachments.download_error'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('attachments.title')}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : attachments.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            {t('attachments.none')}
          </Typography>
        ) : (
          <List dense>
            {attachments.map((attachment) => (
              <ListItem key={attachment.id}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <AttachFile fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={attachment.name}
                  secondary={formatFileSize(attachment.size)}
                />
                <ListItemSecondaryAction>
                  <IconButton size="small" onClick={() => handleDownload(attachment)}>
                    <Download fontSize="small" />
                  </IconButton>
                  {!readOnly && (
                    <IconButton size="small" onClick={() => handleDelete(attachment.id)} color="error">
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        {!readOnly && (
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleUpload}
              style={{ display: 'none' }}
            />
            <Button
              startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {t('attachments.upload')}
            </Button>
          </>
        )}
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
